<template>
  <div class="flex flex-col min-h-screen">
    <geo-watcher />
    <!-- Header -->
    <header>
      <div class="app-header gradient px-6 py-4">
        <div class="max-w-none m-auto">
          <div class="flex justify-between items-center">
            <div class="flex items-center">
              <h5 data-testid="title" class="font-zen-dots text-white">
                <div class="app-logo-container w-[150px] md:w-[200px]">
                  <!-- For MoneyClub -->
                  <img v-if="!isPhoenix" 
                       src="/images/logo-moneyclub.png" 
                       class="h-auto max-w-full"
                       alt="Money Club">
                       
                  <!-- For Stepping Stone -->
                  <img v-else
                       src="/images/logo-steppingstone.png" 
                       class="h-auto max-w-full"
                       alt="Stepping Stone Loans">
                </div>
              </h5>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex flex-col text-center items-center justify-center mx-auto flex-grow p-4" style="background-color: #f5f7fa;">
      <!-- Progress Bar -->
      <div id="progress-container" class="w-full md:max-w-[800px] md:min-w-[350px] mx-auto mb-6"></div>

      <!-- Header Sentence -->
      <div class="info-content mb-2">
        <h1 class="font-medium text-xl md:text-2xl max-w-[930px] leading-[25px] md:leading-[30px]">
          Bank Information
        </h1>
        <h2 class="my-3 md:my-5 text-[#49878c] text-xl md:text-2xl">
          Please provide your bank details to continue
        </h2>
      </div>

      <!-- Form Container -->
      <div class="normal-forms bg-white shadow-md rounded-lg p-6 mx-auto text-left max-w-[600px]">
        <form @submit.prevent="submitForm" class="space-y-6">
          <!-- Bank Name -->
          <div class="form-item w-full">
            <label for="official_name" class="block font-semibold mb-3">Bank Name</label>
            <input 
              type="text" 
              id="official_name" 
              v-model="formData.official_name" 
              class="input-field w-full form-input rounded-lg border-2 border-gray-300 focus:border-gray-500 focus:outline-none px-4 py-2 transition-colors"
              :class="{ 'border-red-300': errors.official_name }"
              placeholder="Enter your bank name"
              
            >
            <p v-if="errors.official_name" class="validation-error-message text-danger mt-2">
              {{ errors.official_name[0] }}
            </p>
          </div>

          <!-- Account Number and Confirmation -->
          <div class="form-group flex gap-4">
            <div class="form-item w-full">
              <label for="account_number" class="block font-semibold mb-3">Account Number</label>
              <input 
                type="text" 
                id="account_number" 
                v-model="formData.account_number" 
                class="input-field w-full form-input rounded-lg border-2 border-gray-300 focus:border-gray-500 focus:outline-none px-4 py-2 transition-colors"
                :class="{ 'border-red-300': errors.account_number }"
                placeholder="Enter account number"
                
              >
              <p v-if="errors.account_number" class="validation-error-message text-danger mt-2">
                {{ errors.account_number[0] }}
              </p>
            </div>
          </div>

          <!-- Routing Number and Confirmation -->
          <div class="form-group flex gap-4">
            <div class="form-item w-full">
              <label for="routing_number" class="block font-semibold mb-3">Routing Number</label>
              <input 
                type="text" 
                id="routing_number" 
                v-model="formData.routing_number" 
                class="input-field w-full form-input rounded-lg border-2 border-gray-300 focus:border-gray-500 focus:outline-none px-4 py-2 transition-colors"
                :class="{ 'border-red-300': errors.routing_number }"
                placeholder="9-digit routing number"
                
              >
              <p v-if="errors.routing_number" class="validation-error-message text-danger mt-2">
                {{ errors.routing_number[0] }}
              </p>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="submitError" class="rounded-md p-4 bg-red-50">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">{{ submitError }}</h3>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button 
            type="submit" 
            id="submit-button"
            class="g-recaptcha bg-action mx-auto block px-8 py-3 flex justify-center items-center shadow-action font-semibold text-lg rounded mt-5 text-white min-w-[200px]"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="mr-2">
              <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            <span>{{ isSubmitting ? 'Processing...' : 'Submit Bank Information' }}</span>
          </button>

          <!-- Trust Badges -->
          <div class="checks flex flex-col md:flex-row w-full justify-center text-[#49878c] mt-12 my-1 text-xs md:text-base">
            <div class="item-check flex-1 text-center md:border-b-2 rounded-br-lg border-[#49878c] px-1 py-1 mx-0 md:mx-8 mb-4 md:mb-0">
              <i class="fas fa-check"></i> Safe & Secure
            </div>
            <div class="item-check flex-1 text-center md:border-b-2 rounded-br-lg border-[#49878c] px-1 py-1 mx-0 md:mx-8 mb-4 md:mb-0">
              <i class="fas fa-check"></i> 60 Second Approval
            </div>
          </div>

          <!-- Footer -->
          <div class="flex mt-4 justify-center">
            <div class="norton-secured-image">
              <img src="/images/norton-secured.png" width="100" alt="Norton Secured" class="h-auto max-w-full">
            </div>
          </div>
          
          <div class="footer mt-8 text-center">
            <p class="text-gray-500 text-sm">Copyright © {{ new Date().getFullYear() }}. All rights reserved.</p>
          </div>
        </form>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import GeoWatcher from "../../../components/GeoWatcher";

export default {
    components: {
        GeoWatcher
    },
    name: 'AdditionalBankInformation',
    data() {
        return {
            formData: {
                official_name: '',
                account_number: '',
                routing_number: '',
                is_changing_bank_flow: false
            },
            errors: {},
            isSubmitting: false,
            submitError: null,
            loan: null,
            isPhoenix: false
        };
    },
    mounted() {
        this.isPhoenix = window.theme == 'phoenix'
        this.getLoanId();
        this.initProgressBar();
    },
    methods: {
        getLoanId() {
            // Get loan ID from URL path
            const pathParts = window.location.pathname.split('/');
            const loanIndex = pathParts.findIndex(part => part === 'welcome') || pathParts.findIndex(part => part === 'loans');
            if (loanIndex !== -1 && loanIndex < pathParts.length - 1) {
                this.loan = pathParts[loanIndex + 1];
                console.log('Loan ID:', this.loan);
            }
        },
        initProgressBar() {
            const steps = ['Registration', 'Basic Info', 'Bank Info'];
            const currentStepIndex = 2; // Bank Info is the 3rd step (index 2)
            
            // Define the ProgressBar class (similar to progress-js.blade.php)
            class ProgressBar {
                constructor(containerId, steps, currentIndex) {
                    this.container = document.getElementById(containerId);
                    this.steps = steps;
                    this.currentIndex = currentIndex;
                    this.alignment = window.innerWidth >= 330 ? 'horizontal' : 'vertical';
                    
                    this.render();
                    window.addEventListener('resize', this.handleResize.bind(this));
                }
                
                handleResize() {
                    const newAlignment = window.innerWidth >= 330 ? 'horizontal' : 'vertical';
                    if (newAlignment !== this.alignment) {
                        this.alignment = newAlignment;
                        this.render();
                    }
                }
                
                render() {
                    const isHorizontal = this.alignment === 'horizontal';
                    
                    const container = document.createElement('div');
                    container.className = `flex ${isHorizontal ? 'flex-row space-x-4 my-4' : 'flex-col space-y-4 my-8'}`;
                    
                    this.steps.forEach((step, index) => {
                        const stepContainer = document.createElement('div');
                        stepContainer.className = `flex items-center relative ${isHorizontal ? 'flex-1 flex-col' : ''}`;
                        
                        if (isHorizontal) {
                            const label = document.createElement('span');
                            label.className = 'text-sm font-medium mb-3 text-[#000000]';
                            label.textContent = step;
                            stepContainer.appendChild(label);
                        }
                        
                        // Add SVG icon based on step state
                        stepContainer.appendChild(this.createStepIcon(index));
                        
                        // Add connector line if not last step
                        if (index < this.steps.length - 1) {
                            const line = document.createElement('div');
                            line.className = isHorizontal ? 'line-horizontal left-[56%] sm:left-[55%] md:left-[54%]' : 'line-vertical';
                            line.style.width = '94%';
                            line.style.height = isHorizontal ? '3px' : '10px';
                            line.style.background = '#74979a';
                            line.style.position = 'absolute';
                            line.style.top = isHorizontal ? '35px' : '20px';
                            line.style.left = isHorizontal ? '56%' : '7px';
                            stepContainer.appendChild(line);
                        }
                        
                        // Add label for vertical layout
                        if (!isHorizontal) {
                            const label = document.createElement('span');
                            label.className = 'text-sm font-medium text-[#000000]';
                            label.textContent = step;
                            stepContainer.appendChild(label);
                        }
                        
                        container.appendChild(stepContainer);
                    });
                    
                    // Clear previous content and add new
                    this.container.innerHTML = '';
                    this.container.appendChild(container);
                }
                
                createStepIcon(index) {
                    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                    svg.setAttribute('width', '16');
                    svg.setAttribute('height', '16');
                    svg.setAttribute('viewBox', '0 0 16 16');
                    svg.classList.add('mr-2');
                    
                    if (index < this.currentIndex) {
                        // Completed step
                        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                        circle.setAttribute('cx', '8');
                        circle.setAttribute('cy', '8');
                        circle.setAttribute('r', '8');
                        circle.setAttribute('fill', '#74979a');
                        svg.appendChild(circle);
                        
                        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                        path.setAttribute('d', 'M11.3337 5.5L6.75033 10.0833L4.66699 8');
                        path.setAttribute('stroke', '#ffffff');
                        path.setAttribute('stroke-width', '1.75');
                        path.setAttribute('stroke-linecap', 'round');
                        path.setAttribute('stroke-linejoin', 'round');
                        svg.appendChild(path);
                    } else if (index === this.currentIndex) {
                        // Current step
                        const outerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                        outerCircle.setAttribute('cx', '8');
                        outerCircle.setAttribute('cy', '8');
                        outerCircle.setAttribute('r', '7');
                        outerCircle.setAttribute('stroke', '#74979a');
                        outerCircle.setAttribute('stroke-width', '2');
                        outerCircle.setAttribute('fill', 'none');
                        svg.appendChild(outerCircle);
                        
                        const innerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                        innerCircle.setAttribute('cx', '8');
                        innerCircle.setAttribute('cy', '8');
                        innerCircle.setAttribute('r', '3.5');
                        innerCircle.setAttribute('fill', '#74979a');
                        svg.appendChild(innerCircle);
                    } else {
                        // Next step
                        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                        circle.setAttribute('cx', '8');
                        circle.setAttribute('cy', '8');
                        circle.setAttribute('r', '7');
                        circle.setAttribute('stroke', '#74979a');
                        circle.setAttribute('stroke-width', '2');
                        circle.setAttribute('fill', 'none');
                        svg.appendChild(circle);
                    }
                    
                    return svg;
                }
            }
            
            // Create the progress bar after DOM is fully loaded
            setTimeout(() => {
                new ProgressBar('progress-container', steps, currentStepIndex);
            }, 100);
        },
        async submitForm() {

            this.isSubmitting = true;
            this.submitError = null;

            try {
                const response = await axios.post(`/signup/selling-process/${this.loan}/bank-information`, {
                  account_number: this.formData.account_number,
                  routing_number: this.formData.routing_number,
                  official_name: this.formData.official_name,
                  _token: document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                });

                if (response.data.status === 'success') {
                    // Redirect to next step or dashboard
                    window.location.reload();
                } else {
                    this.submitError = 'There was an error processing your request. Please try again.';
                }
            } catch (error) {
                console.error('Error submitting form:', error);
                if (error.response && error.response.data && error.response.data.errors) {
                    this.errors = error.response.data.errors;
                } else {
                    this.submitError = 'There was an unexpected error. Please try again later.';
                }
            } finally {
                this.isSubmitting = false;
            }
        }
    }
};
</script>
